import { useUser } from "@/hooks/useUser"
import { getMasterSystem } from "@/services/getData"
import React, { createContext, useEffect, useState } from "react"
import { useQuery } from "@tanstack/react-query"

type TahunOption = {
  label: string
  value: number
}

type TahunContextType = {
  selectedTahun: TahunOption
  setSelectedTahun: (tahun: TahunOption) => void
}

export const TahunContext = createContext<TahunContextType | undefined>(
  undefined
)

const storageKey = "selectedTahun"
const currentYear = new Date().getFullYear()

const createOption = (year: number): TahunOption => ({
  label: year.toString(),
  value: year,
})

const initialTahun = (): TahunOption => {
  const stored = sessionStorage.getItem(storageKey)
  if (stored) {
    try {
      const parsed = JSON.parse(stored)
      return createOption(Number(parsed.value))
    } catch {
      return createOption(currentYear)
    }
  }
  return createOption(currentYear)
}

export const TahunProvider = ({ children }: { children: React.ReactNode }) => {
  const { user } = useUser()
  const [selectedTahun, setSelectedTahunState] =
    useState<TahunOption>(initialTahun)

  const setSelectedTahun = (tahun: TahunOption) => {
    sessionStorage.setItem(storageKey, JSON.stringify(tahun))
    setSelectedTahunState(tahun)
  }

  const { data: system, isFetched } = useQuery({
    queryKey: ["masterSystem"],
    queryFn: getMasterSystem,
    enabled: !!user,
    retry: false,
  })

  useEffect(() => {
    if (!isFetched || !system) return

    const stored = sessionStorage.getItem(storageKey)
    if (stored) {
      try {
        const parsed = JSON.parse(stored)
        setSelectedTahun(createOption(Number(parsed.value)))
      } catch {
        // fallback to active tahun
      }
    } else {
      const activeTahun = system[0]?.tahun_aktif
      if (activeTahun) setSelectedTahun(createOption(activeTahun))
    }
  }, [isFetched, system])

  return (
    <TahunContext.Provider value={{ selectedTahun, setSelectedTahun }}>
      {children}
    </TahunContext.Provider>
  )
}
